<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">

      <!-- Loader -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-2 text-muted">Chargement de la composition...</p>
      </div>

      <div v-else-if="dejaCompose && !composition?.peutEncoreComposer" class="text-center py-5">
        <i class="fa fa-check-circle text-success" style="font-size: 60px;"></i>
        <h4 class="mt-3">Vous avez utilisé tous vos essais</h4>
        <p class="text-muted">
          Vous avez utilisé les <strong>{{ composition?.nombreEssaisAutorise }}</strong> essai(s) autorisé(s) pour cette composition.
        </p>
        <router-link to="/liste-compositions" class="btn btn-outline-primary mt-2">
          Retour à la liste
        </router-link>
      </div>

      <div v-else-if="composition">

        <div v-if="dejaCompose && composition?.peutEncoreComposer" class="alert alert-info text-center mb-4">
          <i class="fa fa-info-circle me-2"></i>
          <strong>Vous avez déjà composé</strong><br>
          <p class="mb-2 mt-2">
            Vous avez utilisé <strong>{{ composition.tentativesEffectuees }}</strong> essai(s) sur <strong>{{ composition.nombreEssaisAutorise }}</strong>.<br>
            Il vous reste encore <strong>{{ composition.tentativesRestantes }}</strong> tentative(s).
          </p>
          <p class="alert alert-warning d-inline-block px-4 py-2 mb-0">
            <i class="fa fa-exclamation-triangle me-2"></i>
            <strong>Important :</strong> Seule la note du dernier essai sera retenue.
          </p>
        </div>

        <!-- En-tête -->
        <div class="d-flex align-items-center justify-content-between mb-30">
          <div>
            <h4 class="fw-bold mb-5">{{ composition.titre }}</h4>
            <span class="badge bg-primary fs-13">{{ composition.type }}</span>
          </div>
          <router-link to="/liste-compositions" class="btn btn-outline-secondary btn-sm">
            <i class="fa fa-arrow-left me-1"></i> Retour
          </router-link>
        </div>

        <!-- Infos -->
        <div class="row mb-30">
          <div class="col-md-6 col-lg-3 mb-15">
            <div class="p-15 rounded-2 border text-center">
              <i class="fa fa-calendar text-primary mb-5" style="font-size:22px;"></i>
              <p class="text-muted mb-0 fs-13">Date de composition:</p>
              <strong>{{ formatDate(composition.dateComposition) }}</strong>
            </div>
          </div>
          <div class="col-md-6 col-lg-3 mb-15">
            <div class="p-15 rounded-2 border text-center">
              <i class="fa fa-clock-o text-warning mb-5" style="font-size:22px;"></i>
              <p class="text-muted mb-0 fs-13">Date limite</p>
              <strong>{{ formatDate(composition.dateFinSoumission || composition.dateComposition) }}</strong>
            </div>
          </div>
          <div class="col-md-6 col-lg-3 mb-15">
            <div class="p-15 rounded-2 border text-center">
              <i class="fa fa-book text-success mb-5" style="font-size:22px;"></i>
              <p class="text-muted mb-0 fs-13">Matière</p>
              <strong>{{ composition.filiereNiveauMatiere?.matiere?.libelle || '-' }}</strong>
            </div>
          </div>
          <div class="col-md-6 col-lg-3 mb-15">
            <div class="p-15 rounded-2 border text-center">
              <i class="fa fa-list-ol text-info mb-5" style="font-size:22px;"></i>
              <p class="text-muted mb-0 fs-13">Nombre de questions</p>
              <strong>{{ composition.compoQuestions?.length || 0 }} questions</strong>
            </div>
          </div>
        </div>

        <!-- Professeur -->
        <div class="border rounded-2 p-15 mb-25 d-flex align-items-center gap-10">
          <i class="fa fa-user-circle text-primary" style="font-size:36px;"></i>
          <div>
            <p class="mb-0 text-muted fs-13">Professeur responsable</p>
            <strong>{{ fullName(composition.professeur) }}</strong>
          </div>
        </div>

        <!-- Instructions -->
        <div class="alert alert-warning d-flex align-items-start gap-10 mb-25">
          <i class="fa fa-exclamation-triangle mt-1"></i>
          <div>
            <strong>Instructions importantes :</strong>
            <ul class="mb-0 mt-5 ps-15">
              <li>Vous disposez de <strong>{{ composition.dureeMinutes }} minutes</strong> pour répondre à toutes les questions.</li>
              <li>Le formulaire se soumettra <strong>automatiquement</strong> à la fin du temps imparti.</li>
              <li>Une fois la salle ouverte, vous ne pouvez pas revenir en arrière.</li>
              <li>Assurez-vous d'être dans un endroit calme avant de commencer.</li>
            </ul>
          </div>
        </div>

        <!-- Expirée -->
        <div v-if="estExpiree" class="alert alert-danger text-center">
          <i class="fa fa-ban me-2"></i>
          <strong>Cette composition est expirée.</strong> Vous ne pouvez plus y participer.
        </div>

        <div v-else-if="dejaCompose && composition?.peutEncoreComposer" class="alert alert-info text-center mb-20">
          <i class="fa fa-redo me-2"></i>
          <strong>Nouvelle tentative :</strong> Vous êtes sur le point de commencer l'essai n°<strong>{{ (composition.tentativesEffectuees || 0) + 1 }}</strong>.
        </div>

        <div v-if="!estExpiree && (!dejaCompose || composition?.peutEncoreComposer)" class="text-center mt-20">
          <button
            class="btn btn-success btn-lg px-40 fw-bold"
            @click="entrerDansLaSalle"
          >
            <i class="fa fa-sign-in me-8"></i>
            {{ dejaCompose ? 'Recommencer la composition' : 'Entrer dans la salle' }}
          </button>
        </div>

      </div>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/services/ApiService";
import { error } from "@/utils/utils";

export default defineComponent({
  name: "ComposerDetailPage",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const composition = ref<any>(null);
    const loading = ref(true);
    const dejaCompose = ref(false);

    onMounted(async () => {
      await fetchComposition();
    });

 async function fetchComposition() {
  try {
    const { data } = await ApiService.get(`/compositions/${route.params.id}/mon-devoir`);
    composition.value = data.data;
    if (data.data?.dejaSoumis) {
      dejaCompose.value = true;
    }
  } catch (e: any) {
    error(e?.response?.data?.message || "Erreur lors du chargement");
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await fetchComposition();
});

onMounted(async () => {
  await fetchComposition();
});

onMounted(async () => {
  await fetchComposition();
});



    const estExpiree = computed(() => {
      if (!composition.value) return false;
      const dateFin = composition.value.dateFinSoumission || composition.value.dateComposition;
      if (!dateFin) return false;
      const fin = new Date(dateFin);
      fin.setDate(fin.getDate() + 1);
      fin.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return fin < today;
    });

    function entrerDansLaSalle() {
      router.push({ name: 'ComposerQuizPage', params: { id: route.params.id } });
    }

    const formatDate = (date: string | null): string =>
      date ? new Date(date).toLocaleDateString("fr-FR") : "-";

    const fullName = (prof: any): string => {
      if (!prof) return "-";
      return `${prof.nom || ""} ${prof.prenom || ""}`.trim() || "Inconnu";
    };

    return {
      composition,
      loading,
      estExpiree,
      entrerDansLaSalle,
      formatDate,
      fullName,
      dejaCompose
    };
  },
});
</script>